"use client";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { CART_MINT, CART_MINT_DARK } from "./cartUi";
import { scheduleHeaderGradient } from "../../genericComponents/date-and-time-picker/scheduleUi";
import Image from "next/image";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckIcon from "@mui/icons-material/Check";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import { ApiService } from "@/app/services/AppService";
import { getToken } from "@/app/services/HolderService";
import {
  RecommendedItem,
  ResponseObject,
  ViewDetailsServices,
} from "@/app/classes/main";
import ViewDetails from "../../genericComponents/view-details/ViewDetails";
import { AddToCartService } from "@/app/services/cart";
import "./index.css";
import { useCurrencyCode } from "../../genericComponents/helper-functions/CurrencySymbol";
import { Loader } from "../../genericComponents/MainLoader/Loader";

interface CartItem {
  sid: string | number;
  count: string | number;
}
interface RecommendedProps {
  cartItems: {
    cartItemRequests: CartItem[];
  } | null;
  updateCartFlag: any;
}

export const Recommended: React.FC<RecommendedProps> = ({
  cartItems,
  updateCartFlag,
}) => {
  const [openInfo, setOpenInfo] = useState(false);
  const [recommendedData, setRecommendedData] = useState<RecommendedItem[]>([]);
  const [viewDetails, setViewDetails] = useState<
    ViewDetailsServices | undefined
  >(undefined);
  const [loading, setLoading] = useState(false);
  const apiService: ApiService = useMemo(() => new ApiService(), []);
  const token = getToken();
  const cartPayload = useMemo(
    () => cartItems?.cartItemRequests ?? [],
    [cartItems?.cartItemRequests]
  );
  const filteredCartpayload = cartPayload?.map(({ sid, count }) => ({
    sid,
    quantity: count,
  }));
  const addTocartService = new AddToCartService();
  const currencyCode = useCurrencyCode();

  const getRecommendServices = useCallback(async () => {
    const payload = { cartRecommendedItemRequests: filteredCartpayload };
    try {
      if (filteredCartpayload?.length > 0) {
        const responseObject: ResponseObject | undefined =
          await apiService.post<ResponseObject>(
            apiService.cartRecommendService,
            payload,
            token!
          );
        const serviceDataOneRs = responseObject?.object;
        setRecommendedData(serviceDataOneRs);
      }
    } catch (error) {
      console.log("Error:", error);
    }
  }, [filteredCartpayload, apiService, token]);

  useEffect(() => {
    getRecommendServices();
  }, [cartPayload]);

  // ---------------- check this method -----------
  const handleAddGift = async (item: any, e: any, index: number) => {
    setLoading(true);
    e.stopPropagation();
    const serviceAvailableIncart = addTocartService.checkIfAvailableinCart(
      item?.id,
      item?.cid
    );
    if (serviceAvailableIncart) {
      await addTocartService.addToCart(
        item?.id,
        false,
        item?.cid,
        item?.coverImage?.secureHttpPath,
        item?.aboutList,
        item?.subHeading,
        item?.serviceCharge,
        item?.productCost,
        item?.min,
        item?.specialPrice,
        item?.specialMessage
      );
    } else {
      await addTocartService.addToCart(
        item?.id,
        true,
        item?.cid,
        item?.coverImage?.secureHttpPath,
        item?.aboutList,
        item?.subHeading,
        item?.serviceCharge,
        item?.productCost,
        item?.min,
        item?.specialPrice,
        item?.specialMessage
      );
    }
    // Create a new copy of bestServiceData with updated inCart value
    let newRecommendedData = { ...recommendedData };
    newRecommendedData = recommendedData?.map((item, idx) => {
      if (idx === index) {
        return {
          ...item,
          inCart: addTocartService.checkIfAvailableinCart(item?.id, item?.cid),
        };
      }
      return item;
    });
    setRecommendedData(newRecommendedData);
    updateCartFlag();
    setLoading(false);
  };

  const handleOpenInfiDialog = (item: any) => {
    setOpenInfo(true);
    setViewDetails(item);
  };

  const reloadCartItemsFlag = () => {
    updateCartFlag();
  };

  return (
    <>
      {loading && <Loader />}
      {recommendedData && recommendedData.length > 0 && (
        <Box
          sx={{
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            mt: 0,
            borderRadius: { xs: "20px", sm: "22px" },
            border: `1px solid ${alpha(CART_MINT, 0.14)}`,
            bgcolor: alpha("#fff", 0.96),
            boxShadow: `0 10px 36px ${alpha(CART_MINT_DARK, 0.07)}, 0 0 0 1px rgba(255,255,255,0.9) inset`,
            /* Do not use overflow:hidden on this root — iOS can fail to pan nested overflow-x strips */
          }}
        >
          <Box
            sx={{
              background: scheduleHeaderGradient,
              px: { xs: 1.25, sm: 2 },
              py: { xs: 1.15, sm: 1.5 },
              display: "flex",
              alignItems: "flex-start",
              gap: 1.25,
              overflow: "hidden",
              borderTopLeftRadius: { xs: "20px", sm: "22px" },
              borderTopRightRadius: { xs: "20px", sm: "22px" },
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                bgcolor: alpha("#fff", 0.18),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <BoltOutlinedIcon sx={{ color: "#fff", fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0, textAlign: "left" }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 800,
                  letterSpacing: "-0.02em",
                  color: "#fff",
                  lineHeight: 1.25,
                  fontSize: { xs: "1.05rem", sm: "1.15rem" },
                }}
              >
                Last-minute choice
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 0.35,
                  color: alpha("#fff", 0.88),
                  lineHeight: 1.35,
                  maxWidth: 520,
                }}
              >
                Quick add-ons that pair well with your cart — same visit, one checkout.
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              width: "100%",
              minWidth: 0,
              pt: { xs: 1.1, sm: 1.5 },
              pb: { xs: 0.65, sm: 1 },
              bgcolor: alpha("#fff", 0.96),
              borderBottomLeftRadius: { xs: "20px", sm: "22px" },
              borderBottomRightRadius: { xs: "20px", sm: "22px" },
            }}
          >
            {/* Scrollport: scroll + translateZ helps iOS; avoid touchAction:pan-x (conflicts with vertical page scroll) */}
            <Box
              sx={{
                width: "100%",
                minWidth: 0,
                maxWidth: "100%",
                overflowX: "auto",
                overflowY: "hidden",
                WebkitOverflowScrolling: "touch",
                overscrollBehaviorX: "contain",
                pb: 0.25,
                transform: "translateZ(0)",
                "&::-webkit-scrollbar": { height: 5 },
                "&::-webkit-scrollbar-thumb": {
                  borderRadius: 999,
                  background: alpha(CART_MINT, 0.28),
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "nowrap",
                  gap: { xs: 1.25, sm: 1.5 },
                  width: "max-content",
                  minWidth: "min-content",
                  px: { xs: 1, sm: 1.75 },
                  boxSizing: "content-box",
                }}
              >
              {recommendedData?.map((item, index) => (
                <Box
                  key={item?.id ?? `rec-${index}`}
                  sx={{
                    flexShrink: 0,
                    width: { xs: "10.75rem", sm: "11.25rem" },
                    borderRadius: "16px",
                    overflow: "hidden",
                    position: "relative",
                    border: `1px solid ${alpha(CART_MINT, 0.12)}`,
                    boxShadow: `0 8px 24px ${alpha(CART_MINT_DARK, 0.08)}`,
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: `0 12px 28px ${alpha(CART_MINT_DARK, 0.12)}`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      aspectRatio: "11 / 10",
                      bgcolor: alpha(CART_MINT, 0.06),
                      isolation: "isolate",
                    }}
                  >
                    <Image
                      src={item?.coverImage?.secureHttpPath || ""}
                      fill
                      sizes="(max-width: 600px) 40vw, 180px"
                      style={{
                        objectFit: "cover",
                      }}
                      alt={item?.name ? String(item.name) : "Recommended service"}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        background:
                          "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.88) 100%)",
                        pointerEvents: "none",
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenInfiDialog(item);
                      }}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        width: 32,
                        height: 32,
                        bgcolor: alpha("#fff", 0.22),
                        backdropFilter: "blur(8px)",
                        color: "#fff",
                        "&:hover": { bgcolor: alpha("#fff", 0.32) },
                      }}
                      aria-label="Service details"
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                    </IconButton>

                    <Box
                      sx={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        p: 1.1,
                        textAlign: "left",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#fff",
                          fontWeight: 700,
                          lineHeight: 1.25,
                          fontSize: "0.8rem",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          mb: 0.25,
                        }}
                      >
                        {item?.name}
                      </Typography>
                      {item?.subHeading ? (
                        <Typography
                          variant="caption"
                          sx={{
                            color: alpha("#fff", 0.82),
                            lineHeight: 1.3,
                            mb: 0.75,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {item?.subHeading}
                        </Typography>
                      ) : null}

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-end",
                          justifyContent: "space-between",
                          gap: 0.75,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            component="div"
                            variant="caption"
                            sx={{
                              color: "#fff",
                              fontWeight: 800,
                              fontVariantNumeric: "tabular-nums",
                            }}
                          >
                            {currencyCode}
                            {item?.amount}
                            <Box
                              component="span"
                              sx={{
                                textDecoration: "line-through",
                                color: alpha("#fff", 0.45),
                                fontWeight: 600,
                                ml: 0.75,
                                fontSize: "0.7rem",
                              }}
                            >
                              {currencyCode}
                              {item?.mrp}
                            </Box>
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              display: "block",
                              mt: 0.25,
                              color: alpha("#86efac", 0.95),
                              fontWeight: 700,
                              fontSize: "0.68rem",
                            }}
                          >
                            Save {currencyCode}
                            {item?.discount}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={(e) => handleAddGift(item, e, index)}
                          sx={{
                            width: 34,
                            height: 34,
                            flexShrink: 0,
                            bgcolor: item?.inCart ? alpha("#fff", 0.25) : CART_MINT,
                            color: "#fff",
                            border: item?.inCart
                              ? `1px solid ${alpha("#fff", 0.4)}`
                              : "none",
                            "&:hover": {
                              bgcolor: item?.inCart
                                ? alpha("#fff", 0.35)
                                : alpha(CART_MINT, 0.92),
                            },
                          }}
                          aria-label={item?.inCart ? "Remove from cart" : "Add to cart"}
                        >
                          {item?.inCart ? (
                            <CheckIcon sx={{ fontSize: 20 }} />
                          ) : (
                            <AddIcon sx={{ fontSize: 20 }} />
                          )}
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ))}
              </Box>
            </Box>
          </Box>
        </Box>
      )}
      <ViewDetails
        open={openInfo}
        setClose={() => setOpenInfo(false)}
        imageUrl={
          viewDetails?.banner?.secureHttpPath ??
          viewDetails?.coverImage?.secureHttpPath
        }
        heading={viewDetails?.name}
        id={viewDetails?.id}
        cid={viewDetails?.cid}
        servicedDetails={viewDetails?.aboutList}
        descriptions={viewDetails?.descriptions}
        amount={viewDetails?.amount}
        min={viewDetails?.min}
        subHeading={viewDetails?.subHeading}
        mrp={viewDetails?.mrp}
        specialPrice={viewDetails?.specialPrice}
        specialMessage={viewDetails?.specialMessage}
        aboutList={viewDetails?.aboutList}
        serviceCharge={viewDetails?.serviceCharge}
        productCost={viewDetails?.productCost}
        coin={viewDetails?.cashbackSalontymCoins}
        reloadCartItemsFlag={reloadCartItemsFlag}
        setReloadViewDetailsItem={() => {}}
      />
    </>
  );
};
